import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Send, CheckCircle2, AlertCircle, BookOpen, ExternalLink } from 'lucide-react';
import { getUserEmailSettings, saveUserEmailSettings, testUserEmail, type UserEmailSettings } from '@/services/userEmailService';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface UserEmailConfigurationProps {
  userId: string;
  companyId: string;
}

export const UserEmailConfiguration = ({ userId, companyId }: UserEmailConfigurationProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form state
  const [isEnabled, setIsEnabled] = useState(false);
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [hasExistingKey, setHasExistingKey] = useState(false);

  useEffect(() => {
    loadUserEmailSettings();
  }, [userId, companyId]);

  const loadUserEmailSettings = async () => {
    try {
      setLoading(true);
      const settings = await getUserEmailSettings(userId, companyId);
      
      if (settings) {
        setIsEnabled(settings.is_active);
        setFromName(settings.from_name || '');
        setFromEmail(settings.from_email || '');
        setReplyToEmail(settings.reply_to_email || '');
        
        if (settings.sendgrid_api_key) {
          setSendgridApiKey('••••••••••••');
          setHasExistingKey(true);
        }
      }
    } catch (error: any) {
      console.error('Error loading email settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load email settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSendGrid = async () => {
    try {
      // Validation
      if (isEnabled) {
        if (!sendgridApiKey || sendgridApiKey === '••••••••••••') {
          if (!hasExistingKey) {
            toast({
              title: 'Validation Error',
              description: 'Please enter a SendGrid API key',
              variant: 'destructive',
            });
            return;
          }
        }

        if (!fromEmail) {
          toast({
            title: 'Validation Error',
            description: 'Please enter a from email address',
            variant: 'destructive',
          });
          return;
        }
      }

      setSaving(true);

      const settingsToSave: Partial<UserEmailSettings> = {
        from_name: fromName,
        from_email: fromEmail,
        reply_to_email: replyToEmail,
        is_active: isEnabled,
      };

      // Only include API key if it's been changed
      if (sendgridApiKey && sendgridApiKey !== '••••••••••••') {
        settingsToSave.sendgrid_api_key = sendgridApiKey;
      }

      await saveUserEmailSettings(userId, companyId, settingsToSave);

      toast({
        title: 'Success',
        description: 'SendGrid configuration saved successfully',
      });

      // Reload to get updated settings
      await loadUserEmailSettings();
    } catch (error: any) {
      console.error('Error saving SendGrid settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save SendGrid configuration',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSendGrid = async () => {
    try {
      setTesting(true);
      const result = await testUserEmail();
      
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Test email sent successfully! Check your inbox.',
        });
      } else {
        toast({
          title: 'Test Failed',
          description: result.message || 'Failed to send test email',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error testing email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test email',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl">SendGrid Email Configuration</CardTitle>
        <CardDescription className="text-sm">
          Configure SendGrid for sending emails from your account. This will override company email settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <div>
              Need help setting up SendGrid? Follow our step-by-step guide to configure your account and verify sender identities.
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a 
                  href="/documentation#sendgrid-setup" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  <BookOpen className="h-4 w-4" />
                  View Setup Guide
                </a>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a 
                  href="https://app.sendgrid.com/settings/api_keys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  Get API Key
                </a>
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable-sendgrid">Use SendGrid for Sending Emails</Label>
            <p className="text-sm text-muted-foreground">
              Enable to use your own SendGrid account for sending emails
            </p>
          </div>
          <Switch
            id="enable-sendgrid"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
        </div>

        {isEnabled && (
          <div className="space-y-5 pt-6 border-t border-gray-200">
            <div className="space-y-2">
              <Label htmlFor="sendgrid-api-key">
                SendGrid API Key *
              </Label>
              <Input
                id="sendgrid-api-key"
                type="password"
                value={sendgridApiKey}
                onChange={(e) => setSendgridApiKey(e.target.value)}
                placeholder="SG...."
              />
              <p className="text-xs text-muted-foreground">
                Your API key will be encrypted and stored securely
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="from-name">From Name</Label>
                <Input
                  id="from-name"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="from-email">From Email *</Label>
                <Input
                  id="from-email"
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reply-to">Reply-To Email (Optional)</Label>
              <Input
                id="reply-to"
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="reply@example.com"
              />
            </div>

            {hasExistingKey && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  SendGrid API key is configured. Leave the API key field as-is to keep your existing key.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-6 border-t">
          <Button
            onClick={handleSaveSendGrid}
            disabled={saving}
            className="shadow-md hover:shadow-lg transition-shadow"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Configuration
          </Button>
          
          {isEnabled && hasExistingKey && (
            <Button
              variant="outline"
              onClick={handleTestSendGrid}
              disabled={testing}
              className="shadow-sm hover:shadow-md transition-shadow"
            >
              {testing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Test Email
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
